/**
 * Authentication Helper Functions
 * 
 * Provides utilities for handling Firebase authentication and backend session creation
 */

import { signInWithGoogle } from '@/libs/firebase/auth';
import { firebaseAuth } from '@/libs/firebase/config';

/**
 * Creates a backend session after Firebase authentication
 * @param {import('firebase/auth').User} firebaseUser - Authenticated Firebase user
 * @param {Function} refreshCSRF - Function to refresh CSRF token (optional)
 * @returns {Promise<boolean>} True if session was created successfully, false otherwise
 */
export async function createBackendSession(firebaseUser, refreshCSRF = null) {
  if (!firebaseUser) {
    return false;
  }

  let timeoutId;
  try {
    const idToken = await firebaseUser.getIdToken();

    // Create abort controller for timeout
    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for session
      body: JSON.stringify({ token: idToken }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      await response.json();
      // After successful login, refresh the CSRF token if provided
      if (refreshCSRF && typeof refreshCSRF === 'function') {
        await refreshCSRF();
      }
      return true;
    } else {
      // Only log if it's not a network error (which is expected in some cases)
      if (response.status !== 0) {
        const errorData = await response.text().catch(() => '');
        console.warn('Failed to establish server session. Status:', response.status, errorData ? `Response: ${errorData}` : '');
      }
      return false;
    }
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    // Suppress network errors that are likely from browser extensions or offline state
    if (error.name === 'AbortError' || (error.name === 'TypeError' && error.message.includes('Failed to fetch'))) {
      // Network errors are expected in some scenarios (extensions, offline, etc.)
      // Don't spam console with these - they're handled gracefully
      return false;
    }
    console.error('Error during backend session creation:', error);
    return false;
  }
}

/**
 * Authenticates user with Google and creates backend session
 * Handles the complete authentication flow: Firebase auth → Backend session → Ready for redirect
 * 
 * @param {Function} refreshCSRF - Optional function to refresh CSRF token
 * @returns {Promise<{success: boolean, user: import('firebase/auth').User|null, error: Error|null}>}
 */
export async function authenticateAndCreateSession(refreshCSRF = null) {
  try {
    // Step 1: Firebase authentication
    const firebaseUser = await signInWithGoogle();
    
    // For mobile redirect flow, signInWithGoogle returns null
    // The redirect will handle the session creation on return
    if (!firebaseUser) {
      return { success: false, user: null, error: null }; // Redirect initiated
    }

    // Step 2: Create backend session
    const sessionCreated = await createBackendSession(firebaseUser, refreshCSRF);
    
    if (!sessionCreated) {
      // Session creation failed, but Firebase auth succeeded
      // This is not ideal but we can still proceed - the useUserSession hook will retry
      console.warn('Backend session creation failed, but Firebase auth succeeded. Session may be created automatically.');
    }

    // Step 3: Link anonymous readings after authentication
    try {
      // Get CSRF token for the link request
      const csrfResponse = await fetch('/api/auth/csrf', {
        credentials: 'include',
      });
      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json();
        const csrfToken = csrfData.csrfToken;
        
        // Link anonymous readings
        const linkResponse = await fetch('/api/readings/link-anonymous', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          credentials: 'include',
        });
        
        if (linkResponse.ok) {
          const linkData = await linkResponse.json();
          console.log('[auth-helpers] Link anonymous readings response:', linkData);
          if (linkData.linkedCount > 0) {
            console.log(`✅ Linked ${linkData.linkedCount} anonymous reading(s) to user account`);
          } else {
            console.warn('[auth-helpers] No readings linked:', linkData.message);
          }
        } else {
          const errorData = await linkResponse.json().catch(() => ({}));
          console.error('[auth-helpers] Failed to link anonymous readings:', linkResponse.status, errorData);
        }
      }
    } catch (linkError) {
      // Don't fail authentication if linking fails - it's not critical
      console.warn('Failed to link anonymous readings after authentication:', linkError);
    }

    return { success: true, user: firebaseUser, error: null };
  } catch (error) {
    // Handle Firebase auth errors
    if (error.code === 'auth/popup-closed-by-user') {
      return { success: false, user: null, error: { code: 'popup-closed', message: 'Sign-in popup closed by user.' } };
    } else if (error.code === 'auth/cancelled-popup-request') {
      return { success: false, user: null, error: { code: 'popup-cancelled', message: 'Sign-in popup request cancelled.' } };
    } else if (error.code === 'auth/redirect-cancelled' || error.message?.includes('redirect')) {
      return { success: false, user: null, error: { code: 'redirect-cancelled', message: 'Sign-in redirect operation was cancelled or failed.' } };
    } else {
      console.error('Error during authentication:', error);
      return { success: false, user: null, error: { code: 'unknown', message: 'An error occurred during the sign-in process. Please try again.' } };
    }
  }
}

/**
 * Checks if user is currently authenticated (has both Firebase auth and backend session)
 * @returns {Promise<boolean>} True if user is authenticated
 */
export async function isUserAuthenticated() {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check Firebase auth
  const currentUser = firebaseAuth?.currentUser;
  if (!currentUser) {
    return false;
  }

  // Check backend session by making a lightweight request
  try {
    const response = await fetch('/api/user/dashboard', {
      method: 'GET',
      credentials: 'include',
    });
    return response.status !== 401;
  } catch {
    return false;
  }
}
